'use client';

import Image from '@eliadmin/core/src/components/image/image-wrapped';
import Link from '@eliadmin/core/src/components/link';

import { useId } from 'react';
import { FiChevronDown } from 'react-icons/fi';

import type { TDynamicData } from '../../types/dynamic-data';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';
import { Button } from '../ui/button';
import { Reveal } from '../ui/reveal';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { HtmlRendererRaw } from '@eliadmin/core/src/components/ui/html-renderer-raw';

export function FAQ({ faqs, title }: { faqs: TDynamicData[]; title?: string }) {
  const { t } = useTranslations();
  const groupName = useId();

  return (
    <section className={cn('custom-section', 'py-8 sm:py-10 md:py-12 lg:py-16 xl:py-[72px]')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-8 md:flex-row md:justify-between lg:gap-12 xl:gap-16',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-8 sm:gap-10 md:max-w-[690px] lg:gap-12">
          <Reveal variant="left">
            <Heading
              subtitle="Faq"
              title={title || 'Všetko, čo potrebujete vedieť pred <span>spoluprácou</span>'}
              className={{ container: 'w-full max-w-[446px]' }}
            />
          </Reveal>
          <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3 lg:gap-4">
            {faqs.map(faq => (
              <Reveal key={faq.id} variant="bottom">
                <details name={groupName} className="group w-full">
                  <summary
                    className={cn(
                      'flex cursor-pointer list-none items-center justify-between border-b border-t-0 px-0 py-4 text-left text-base font-bold tracking-tight transition-colors sm:text-lg lg:text-xl [&::-webkit-details-marker]:hidden',
                      'group-open:border-transparent group-open:text-primary',
                      'ea-faq__accordion-trigger',
                    )}
                  >
                    {faq.name}
                    <FiChevronDown
                      aria-hidden="true"
                      className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180 group-open:text-primary motion-reduce:transition-none"
                    />
                  </summary>
                  <div className="border-b border-b-black pb-4 text-sm">
                    <HtmlRendererRaw
                      className={cn('w-full', 'ea-faq__answer-content')}
                      html={(faq.fields ?? [])[0]?.value || ''}
                    />
                  </div>
                </details>
              </Reveal>
            ))}
          </div>
        </div>
        <Reveal width="fit-content" variant="right">
          <div
            className={cn(
              'flex w-full flex-col items-start justify-start gap-4 p-4 sm:max-w-[384px] sm:gap-6 sm:p-6 lg:gap-8 lg:p-8',
              'ea-faqs__contact-card',
            )}
          >
            <Image
              src={'/images/agent.png'}
              alt="Meno Priezvisko"
              width={0}
              height={0}
              className="h-20 w-20 overflow-hidden rounded-full object-cover object-center sm:h-24 sm:w-24 lg:h-[116px] lg:w-[116px]"
              sizes="33vw"
            />
            <div className="flex w-full flex-col items-start justify-start gap-2 sm:gap-3 lg:gap-4">
              <h3 className="text-lg font-bold sm:text-xl">{t('text3590')}</h3>
              <p className="text-sm sm:text-base">
                {t('text3591')}
                <br />
                {t('text2358')}
              </p>
            </div>
            <Button asChild className="w-full">
              <Link href="/kontakt">{t('text186')}</Link>
            </Button>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
